import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, Timer } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwErr } from "./hwApi";
import { tsApi } from "./HomeworkHome";

function PlayerInner() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [cur, setCur] = useState(0);
  const [left, setLeft] = useState(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    tsApi.get(`/attempts/${attemptId}`).then(({ data: d }) => {
      setData(d);
      setAnswers(d.answers || {});
      setLeft(Math.max(0, Math.round(d.expires_at - Date.now() / 1000)));
    }).catch((e) => setError(hwErr(e)));
  }, [attemptId]);

  // countdown + auto-submit at zero
  useEffect(() => {
    if (!data || data.status !== "in_progress") return undefined;
    const t = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          if (!submittedRef.current) { submittedRef.current = true; submit(true); }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);

  const submit = async (auto = false) => {
    if (!auto && !window.confirm("Submit the test? You can't change answers after submitting.")) return;
    try {
      const { data: r } = await tsApi.post("/submit", { attempt_id: attemptId, answers });
      setData((d) => ({ ...d, status: "submitted", score: r.score, time_taken_sec: r.time_taken_sec, per_question: r.per_question || [] }));
    } catch (e) { setError(hwErr(e)); }
  };

  if (error) return <div className="tp-wrap"><p className="tp-error" data-testid="ts-player-error">{error}</p></div>;
  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  const urgent = left <= 60 && data.status === "in_progress";

  /* ---------- result screen ---------- */
  if (data.status === "submitted") {
    const score = data.score || {};
    const reviewQuestions = (data.per_question || []).filter((q) => q && q.text && (q.options || q.solution || q.explanation));
    return (
      <div className="tp-wrap" style={{ maxWidth: 760 }} data-testid="ts-result">
        <div className="tp-eyebrow" style={{ color: "#ea580c" }}>TEST RESULT · {data.scope_label}</div>
        <div className="tp-panel" style={{ marginTop: 12, border: "2px solid #ea580c", background: "#fff7ed", textAlign: "center", padding: 24 }}>
          <b style={{ fontSize: 40, color: "#ea580c" }} data-testid="ts-result-score">{score.correct}/{score.total}</b>
          <p className="tp-muted" style={{ margin: "4px 0 0" }}>{score.percent}% · {Math.floor((data.time_taken_sec || 0) / 60)}m {(data.time_taken_sec || 0) % 60}s taken of {data.duration_min} min</p>
          <div className="tp-grid-4" style={{ marginTop: 14 }} data-testid="ts-result-breakdown">
            {[["Correct", score.correct, "#15803d"], ["Incorrect", score.wrong, "#b42318"], ["Unanswered", score.unanswered, "#b45309"]].map(([l, v, c]) => (
              <div className="tp-card" key={l} style={{ textAlign: "center" }}>
                <b style={{ fontSize: 20, color: c }}>{v}</b>
                <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="ts-result-review">
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Solutions &amp; explanations</h2>
          {data.solutions_locked && (
            <div style={{ background: "linear-gradient(90deg,#0b2545,#133763)", borderRadius: 12, padding: "16px 18px", color: "#fff", margin: "12px 0" }} data-testid="ts-solutions-locked">
              <b>Full solutions are locked for guests</b>
              <p style={{ margin: "6px 0 10px", color: "#c8d4e2", fontSize: 13 }}>Create your free account to see Pranjal Sir's solutions, save your history and get homework checked.</p>
              <a className="tp-btn" href="/homework/auth" style={{ background: "#f77f00", textDecoration: "none" }} data-testid="ts-register-cta">REGISTER FREE — UNLOCK SOLUTIONS</a>
            </div>
          )}
          {reviewQuestions.map((q, i) => (
            <div key={q.question_id || i} style={{ borderTop: "1px solid #e4eaf2", padding: "12px 0" }} data-testid={`ts-rq-${i}`}>
              <div className="admin-row">
                <span className={`status-pill ${q.is_correct ? "approved" : q.chosen ? "failed" : "locked"}`} data-testid={`ts-rq-status-${i}`}>
                  {q.is_correct ? "CORRECT" : q.chosen ? "INCORRECT" : "UNANSWERED"}</span>
                <span className="tp-muted" style={{ fontSize: 12 }}>Q{i + 1}</span>
              </div>
              <p style={{ color: "#0b2545", margin: "8px 0 0", whiteSpace: "pre-wrap" }}>{q.text}</p>
              <div style={{ marginTop: 6 }}>
                {Object.entries(q.options || {}).map(([k, v]) => (
                  <p key={k} className="tp-muted" style={{ margin: "2px 0", fontSize: 13,
                                                           color: k === q.answer ? "#15803d" : k === q.chosen ? "#b42318" : undefined,
                                                           fontWeight: k === q.answer || k === q.chosen ? 600 : 400 }}>
                    {k}) {v} {k === q.answer && "✓ correct"} {k === q.chosen && k !== q.answer && "✗ your choice"}
                  </p>
                ))}
              </div>
              {q.formula && <p className="tp-note" style={{ margin: "6px 0 0" }}><b>Formula:</b> {q.formula}</p>}
              {q.solution && <p className="tp-note" style={{ color: "#0369a1", margin: "6px 0 0" }} data-testid={`ts-rq-solution-${i}`}><b>Solution:</b> {q.solution}</p>}
              {q.explanation && <p className="tp-note" style={{ color: "#475569", margin: "4px 0 0" }}><b>Concept:</b> {q.explanation}</p>}
            </div>
          ))}
        </div>
        <div className="admin-row" style={{ marginTop: 16, gap: 10 }}>
          <a className="tp-btn" style={{ background: "#ea580c", textDecoration: "none" }} href="/homework/tests" data-testid="ts-again">Take another test</a>
          <a className="tp-btn ghost" href="/homework" style={{ textDecoration: "none" }} data-testid="ts-home-link">Dashboard</a>
        </div>
      </div>
    );
  }

  /* ---------- test player ---------- */
  const qs = data.questions || [];
  const q = qs[cur];
  const answeredCount = qs.filter((x) => answers[x.question_id]).length;
  return (
    <div className="tp-wrap" style={{ maxWidth: 760 }} data-testid="ts-player">
      <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="tp-eyebrow" style={{ color: "#ea580c" }}>PRANJAL SIR TEST SERIES · {data.category_label}</div>
          <h1 className="tp-h2" style={{ fontSize: 22, margin: 0 }}>{data.scope_label}</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 12,
                      background: urgent ? "#fef2f2" : "#fff7ed", border: `2px solid ${urgent ? "#dc2626" : "#ea580c"}` }}
             data-testid="ts-timer">
          <Timer size={18} style={{ color: urgent ? "#dc2626" : "#ea580c" }} />
          <b style={{ fontSize: 20, color: urgent ? "#dc2626" : "#9a3412", fontVariantNumeric: "tabular-nums" }}>{mm}:{ss}</b>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 14 }} data-testid="ts-qnav">
        {qs.map((x, i) => (
          <button key={x.question_id} onClick={() => setCur(i)} data-testid={`ts-qnav-${i + 1}`}
                  style={{ width: 36, height: 36, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
                           border: i === cur ? "2px solid #ea580c" : "1px solid #cbd5e1",
                           background: answers[x.question_id] ? "#ffedd5" : "#fff", color: "#0b2545" }}>{i + 1}</button>
        ))}
      </div>
      {q && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid={`ts-q-${cur + 1}`}>
          <div className="admin-row">
            <span className="status-pill" style={{ background: "#ffedd5", color: "#9a3412" }}>Q{cur + 1} of {qs.length}</span>
            <span className="tp-muted" style={{ fontSize: 12 }}>{q.difficulty}</span>
            <span className="tp-muted" style={{ fontSize: 12 }}>{answeredCount}/{qs.length} answered</span>
          </div>
          <p style={{ color: "#0b2545", fontSize: 15, whiteSpace: "pre-wrap" }}>{q.text}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
            {Object.entries(q.options).map(([k, v]) => (
              <button key={k} onClick={() => setAnswers((c) => ({ ...c, [q.question_id]: k }))} data-testid={`ts-opt-${cur + 1}-${k}`}
                      style={{ textAlign: "left", padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 14,
                               border: answers[q.question_id] === k ? "2px solid #ea580c" : "1px solid #cbd5e1",
                               background: answers[q.question_id] === k ? "#fff7ed" : "#fff", color: "#0b2545" }}>
                <b>{k})</b> {v}
              </button>
            ))}
          </div>
          <div className="admin-row" style={{ justifyContent: "space-between", marginTop: 14 }}>
            <button className="tp-btn ghost" disabled={cur === 0} onClick={() => setCur((c) => c - 1)} data-testid="ts-prev"><ChevronLeft size={15} /> Previous</button>
            {cur < qs.length - 1
              ? <button className="tp-btn ghost" onClick={() => setCur((c) => c + 1)} data-testid="ts-next">Next <ChevronRight size={15} /></button>
              : <button className="tp-btn" style={{ background: "#ea580c" }} onClick={() => submit(false)} data-testid="ts-submit"><CheckCircle2 size={15} /> SUBMIT TEST</button>}
          </div>
        </div>
      )}
      {error && <p className="tp-error" role="alert">{error}</p>}
    </div>
  );
}

export default function TestPlayer() {
  return <HomeworkShell><PlayerInner /></HomeworkShell>;
}
